/**
 * 日報表服務
 * 統一處理日報表相關 API，支援 Mock/真實 API 切換
 */

import { config } from './config'
import apiClient from './apiClient'
import { getAllTasks } from './taskService'
import type {
  DailyReportQuery,
  DailyReportResponse,
  DailyReportTaskItem,
  TaskDetailResponse,
} from '@/types'

/**
 * 取得日報表
 * @param query 查詢參數
 */
export async function getDailyReport(query: DailyReportQuery): Promise<DailyReportResponse> {
  if (config.useMock) {
    console.log('[DailyReportService] 使用 Mock: getDailyReport')
    return mockGetDailyReport(query)
  }

  console.log('[DailyReportService] 呼叫 API: getDailyReport')
  try {
    const params = new URLSearchParams({
      startDate: query.startDate,
      endDate: query.endDate,
      queryType: query.queryType || 'dutyDate', // 預設使用班別日期查詢
      page: String(query.page || 1),
      pageSize: String(query.pageSize || 20),
    })
    // 加入時間參數（若有提供）
    if (query.startTime) {
      params.append('startTime', query.startTime)
    }
    if (query.endTime) {
      params.append('endTime', query.endTime)
    }

    const response = await apiClient.get<DailyReportResponse>(
      `/line/drivers/me/daily-reports?${params.toString()}`
    )
    return response
  } catch (error) {
    console.error('[DailyReportService] getDailyReport 失敗:', error)
    // 返回空結果
    return {
      queryRange: {
        startDate: query.startDate,
        endDate: query.endDate,
        isSingleDay: query.startDate === query.endDate,
      },
      summary: {
        totalTasks: 0,
        localTasks: 0,
        crossAreaTasks: 0,
        driveMileage: null,
        oilConsumption: null,
        avgOil: null,
        vehicleNos: null,
      },
      tasks: [],
      pagination: {
        page: query.page || 1,
        pageSize: query.pageSize || 20,
        totalItems: 0,
        totalPages: 0,
      },
    }
  }
}

/**
 * 取得任務詳情
 * @param taskId 任務 ID
 */
export async function getTaskDetail(taskId: string): Promise<TaskDetailResponse | null> {
  if (config.useMock) {
    console.log('[DailyReportService] 使用 Mock: getTaskDetail')
    return mockGetTaskDetail(taskId)
  }

  console.log('[DailyReportService] 呼叫 API: getTaskDetail')
  try {
    const response = await apiClient.get<TaskDetailResponse>(`/line/tasks/${taskId}/detail`)
    return response
  } catch (error) {
    console.error('[DailyReportService] getTaskDetail 失敗:', error)
    return null
  }
}

// ========== Mock 實作 ==========

/**
 * Mock 日報表查詢
 */
function mockGetDailyReport(query: DailyReportQuery): DailyReportResponse {
  const allTasks = getAllTasks()
  const completedTasks = allTasks.filter((t) => t.status === 'COMPLETED')

  // 轉換為日報表格式
  const tasks: DailyReportTaskItem[] = completedTasks.map((task) => ({
    taskId: task.taskId,
    vesselName: task.vesselName || null,
    containerNo: task.containerNumber || null,
    origin: task.origin || null,
    originShortName: null,
    destination: task.destination || null,
    destinationShortName: null,
    regionType: 'LOCAL',
    taskType: 'CY' as const,       // Mock 預設為 CY
    ioType: null,
    dischargePort: null,
    acceptedTime: task.acceptedAt || null,
    completedTime: task.completedAt || null,
    source: 'MISSION',
    amendmentId: null,
    amendmentMode: null,
  }))

  // 分頁
  const page = query.page || 1
  const pageSize = query.pageSize || 20
  const totalItems = tasks.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (page - 1) * pageSize
  const pagedTasks = tasks.slice(startIndex, startIndex + pageSize)

  return {
    queryRange: {
      startDate: query.startDate,
      endDate: query.endDate,
      isSingleDay: query.startDate === query.endDate,
    },
    summary: {
      totalTasks: totalItems,
      localTasks: Math.floor(totalItems * 0.6),
      crossAreaTasks: Math.ceil(totalItems * 0.4),
      driveMileage: 156,
      oilConsumption: 45.2,
      avgOil: 3.45,
      vehicleNos: ['DFA-456'],
    },
    tasks: pagedTasks,
    pagination: {
      page,
      pageSize,
      totalItems,
      totalPages,
    },
  }
}

/**
 * Mock 任務詳情查詢
 */
function mockGetTaskDetail(taskId: string): TaskDetailResponse | null {
  const allTasks = getAllTasks()
  const task = allTasks.find((t) => t.taskId === taskId)

  if (!task) {
    return null
  }

  return {
    taskId: task.taskId,
    status: '30',
    statusName: '已完成',
    ioType: task.importExportType === '進口' ? 'I' : 'E',
    ioTypeName: task.importExportType,
    vesselCode: task.vesselCode || null,
    vesselName: task.vesselName || null,
    containerNo: task.containerNumber || null,
    containerSize: task.containerSize || null,
    emptyLoaded: task.containerType === '空櫃' ? 'E' : 'L',
    emptyLoadedName: task.containerType || null,
    origin: task.origin || null,
    originShortName: task.originShortName || task.origin || null,
    destination: task.destination || null,
    destinationShortName: task.destinationShortName || task.destination || null,
    regionType: 'LOCAL',
    taskType: 'CY',                          // Mock 預設為 CY
    dischargePort: task.dischargePort || null, // 卸貨港
    portCode: task.portCode || null,          // 靠泊碼頭
    requesterCompany: task.assignedCompany || null,
    consignee: task.consignee || null,       // 貨主資訊
    authCode: task.authCode || null,
    notes: task.notes || null,               // 備註
    assignedTime: task.createdAt || null,
    acceptedTime: task.acceptedAt || null,
    startedTime: null,
    completedTime: task.completedAt || null,
    createdAt: task.createdAt || null,
  }
}
